/**
 * @module App
 * @description Our NextJS App
 */
import listOfMonarchComponents from '@monarch/listOfMonarchComponents';
import type { AppProps, NextWebVitalsMetric } from 'next/app';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { ready } from 'ui/js/utils/events';
import { bandwidth, objectSans } from 'ui/js/utils/fonts';
import { withCart } from 'ui/providers/cart';
import { withCustomCohesion } from 'ui/providers/cohesion';
import { withFeatureFlagging } from 'ui/providers/feature-flagging';
import Cookies from 'js-cookie';
import merge from 'lodash-es/merge';
import '@public/rv-assets/fonts/icomoon/style.css';
import 'ui/styles/globals.css';
import '~/styles/theme.css';
import { withCohesion } from '@providers/cohesion-provider';
import { setMonarchMonitoring } from '@red-digital/bricks';
import { CohesionSnippet } from '@redventures/cohesion-utils-react';
import { cohesionSnippet } from 'ui/js/config/cohesion';
import LogRocket from 'logrocket';
import setupLogRocketReact from 'logrocket-react';
import sanitizeNetworkData from '~/helpers/sanitizeNetworkData';

LogRocket.init('vuvnvg/frontier', {
  release: process.env.release,
  network: {
    requestSanitizer: sanitizeNetworkData(['addressKey', 'address']),
    responseSanitizer: sanitizeNetworkData(['addressKey', 'addressLine1', 'addressLine2', 'city', 'stateProvince', 'zipCode', 'zipCode4']),
  },
  dom: {
    privateClassNameBlocklist: ['fs-mask'],
  },
});

const App = ( { Component, pageProps }: AppProps ) => {
  const router = useRouter();
  const loadJS = () => import( 'ui/js/main' ).then( ( module ) => module.default() );

  /**
   * @function useEffect
   * @description setMonarchMonitoring[Bricks Monitoring]
   */
  useEffect( () => {
    setupLogRocketReact(LogRocket);
    setMonarchMonitoring( { hasUpdatedSnippet: true } );
    ready( loadJS );
  }, [] );

  useEffect( () => {
    router.events.on( 'routeChangeComplete', () => {
      tagular( 'pageView' );

      if ( window && window.newrelic ) window.newrelic.interaction().save();
    } );
  }, [ router.events ] );

  useEffect( () => {
    if ( process.env.environment === 'local' || 'a11y' in router.query ) import( '@khanacademy/tota11y' );
  }, [ router.query ] );

  useEffect( () => {
    // List of query params that may be on the URL and we want to save for Marketo
    const paramKeysToSave = [ 'utm_campaign', 'utm_term', 'utm_medium', 'utm_source', 'utm_content', 'gclid' ];
    const currentParams = new URLSearchParams( window.location.search.substring( 1 ) );
    const currentCookieString = Cookies.get( 'frontierSiteDetailPredictive' );
    const currentCookie = currentCookieString ? JSON.parse( currentCookieString ) : {};

    // Object that will be saved to the cookie
    const marketoParams: Record<string, string> = {
      formOriginalUrl: currentCookie?.formOriginalUrl || encodeURI( window.location.href )
    };

    paramKeysToSave.forEach( ( paramKey ) => {
      const paramValue = currentParams.get( paramKey );
      if ( paramValue ) marketoParams[ paramKey ] = paramValue;
    } );

    // Set site detail cookie
    const newCookieData = merge( currentCookie, marketoParams );
    const cookieDomain = process.env.environment === 'production' ? 'frontier.com' : undefined;
    Cookies.set( 'frontierSiteDetailPredictive', JSON.stringify( newCookieData ), { expires: 1, path: '/', domain: cookieDomain } );
  } );

  return (
    <>
      {/* eslint-disable-next-line react/no-unknown-property */}
      <style jsx global>
        {`
          :root {
            --font-bandwidth: ${ bandwidth.style.fontFamily }, "Helvetica",
              "Arial", sans-serif;
            --font-primary: ${ objectSans.style.fontFamily }, "Helvetica", "Arial",
              sans-serif;
          }
        `}
      </style>
      {/* Block lists will look for the pattern "/cohesion-latest.", in URLs. So by naming
        the script something different it will get blocked even less often. */}
      <CohesionSnippet config={ cohesionSnippet } src="https://business.frontier.com/rv-assets/unity-newest.min.js" />
      <Component { ...pageProps } />
    </>
  );
};

export const reportWebVitals = ( metric: NextWebVitalsMetric ) => {
  if ( process.env.environment !== 'production' ) return;
  if ( metric.label === 'web-vital' ) import( 'ui/js/utils/cwv' ).then( ( module ) => module.default( metric ) );
};

App.displayName = 'App';


const appHOC1 = withFeatureFlagging( { App } );
const appHOC2 = withCart( { App: appHOC1 } );
const appHOC3 = withCustomCohesion( { App: appHOC2 } );

const appHOC4 = withCohesion( {
  App: appHOC3,
  assets: listOfMonarchComponents as any
} );

export default appHOC4;
